import { DateTime, IANAZone } from 'luxon';

/* The policy rules the redesigned policy header, its Policy Details cards and the Policies list
   share: status, the term and how it reads (in the policy's time zone), and renewal availability.
   Neutral ground, so neither the new policy page nor the list reaches into another page for them. */

/** A policy date: epoch milliseconds from the API, or ISO 8601 (the integration fixtures). */
export type PolicyDate = number | string;

/** The fields of GET /policies/{id} the header and the Policy Details cards read. */
export type PolicySummary = {
  id: number | string;
  policyNumber?: string;
  insured: string;
  product?: { name?: string | null } | null;
  effectiveAt: PolicyDate;
  effectiveUntil: PolicyDate;
  canceledAt?: PolicyDate | null;
  payoutsEnabled?: boolean | null;
  /** The customer's IANA zone, which the term dates belong to. */
  timezone?: string | null;
  /** GWP at binding: the base policy's location coverages, with no endorsements. */
  netPremium?: number | null;
  /** Total insured value. */
  coveredValue?: number | null;
  quoteRate?: number | null;
  /** Set exactly when the policy is per-occurrence. */
  perOccurrenceDeductible?: number | null;
  fees?: { name: string; amount: number }[];
  locations?: { coverages: { netPremium: number }[] }[];
};

export type PolicyStatus = 'Active' | 'Inactive' | 'Cancelled';

/** Running, with the calendar days left to the end date (0 on and after it), or cancelled on a date. There is no expired state. */
export type TermState = { kind: 'running'; daysLeft: number } | { kind: 'cancelled'; on: DateTime };

export type RenewalAvailability = { available: boolean; opensOn: DateTime };

// | undefined spelled out: tsconfig has exactOptionalPropertyTypes, and the Policies list passes
// props it destructured (canceledAt?: … | undefined).
export type StatusFields = { canceledAt?: PolicyDate | null | undefined; payoutsEnabled?: boolean | null | undefined };

const AHS_PRODUCTS = ['Auto Hail-Safe-Max10', 'Auto Hail-Safe'];

/** Renewal opens when this many calendar days or fewer remain. */
const RENEWAL_WINDOW_DAYS = 90;

/**
 * A policy date as the policy page shows it, in the zone the DateTime carries, with that zone's
 * abbreviation: "Mar 1, 2026 CST". US English whatever the browser's locale, so the abbreviation is
 * always "CST"/"EDT", never "GMT-4".
 */
export const formatPolicyDate = (date: DateTime) => date.setLocale('en-US').toFormat('LLL d, yyyy ZZZZ');

/** The policy's timezone, when the response has one luxon knows. */
const policyZone = (policy: PolicySummary): string | null => (policy.timezone && IANAZone.isValidZone(policy.timezone) ? policy.timezone : null);

/** The zone a policy's term dates show in: the policy's when the response has one, else the viewer's. */
export function termZone(policy: PolicySummary): string {
  return policyZone(policy) ?? DateTime.local().zoneName;
}

/** A term date (start, end, cancellation) in the policy's zone. */
export function termDate(value: PolicyDate, policy: PolicySummary): DateTime {
  return toDateTime(value).setZone(termZone(policy));
}

/** The term as the policy page shows it: "{start} – {end}", each in the policy's zone with its abbreviation. */
export function termRange(policy: PolicySummary): string {
  return `${formatPolicyDate(termDate(policy.effectiveAt, policy))} – ${formatPolicyDate(termDate(policy.effectiveUntil, policy))}`;
}

/** The tooltip for a term date: which zone it is in. */
export function termZoneNote(policy: PolicySummary): string {
  const zone = policyZone(policy);
  return zone ? `Dates in the policy's time zone, ${zone}` : 'Dates in your time zone';
}

/** Whether the term state (days left, or cancelled) shows: not when the status is Inactive. */
export function showsTermState(policy: PolicySummary, now: DateTime): boolean {
  return policyStatus(policy, now) !== 'Inactive';
}

/** The term state in words: "{n} days left", "1 day left", "Expires today" or "Cancelled {date}". */
export function termLabel(state: TermState): string {
  switch (state.kind) {
    case 'cancelled':
      return `Cancelled ${formatPolicyDate(state.on)}`;
    default:
      if (state.daysLeft === 0) return 'Expires today';
      return state.daysLeft === 1 ? '1 day left' : `${state.daysLeft} days left`;
  }
}

/** A number is epoch milliseconds; a string is ISO 8601. */
export function toDateTime(value: PolicyDate): DateTime {
  return typeof value === 'number' ? DateTime.fromMillis(value) : DateTime.fromISO(value);
}

/** Cancelled once canceledAt is set and is at or before now. */
export function isCancelled(policy: StatusFields, now: DateTime = DateTime.now()): boolean {
  return policy.canceledAt != null && toDateTime(policy.canceledAt) <= now;
}

/** Cancelled, else Active when payouts are enabled, else Inactive. */
export function policyStatus(policy: StatusFields, now: DateTime = DateTime.now()): PolicyStatus {
  if (isCancelled(policy, now)) return 'Cancelled';
  return policy.payoutsEnabled ? 'Active' : 'Inactive';
}

/**
 * Calendar days from today to the end date, both in the policy's zone, floored at 0: 0 on the end date
 * and after it. A daylight-saving change doesn't move the count off the calendar.
 */
export function daysRemaining(policy: PolicySummary, now: DateTime): number {
  const end = termDate(policy.effectiveUntil, policy).startOf('day');
  const today = now.setZone(termZone(policy)).startOf('day');
  // Math.max(0, …) also turns a -0 into 0.
  return Math.max(0, Math.round(end.diff(today, 'days').days));
}

/** Cancelled (on canceledAt, in the policy's zone), else running with the calendar days left. */
export function termState(policy: PolicySummary, now: DateTime): TermState {
  if (policy.canceledAt != null && isCancelled(policy, now)) {
    return { kind: 'cancelled', on: termDate(policy.canceledAt, policy) };
  }
  return { kind: 'running', daysLeft: daysRemaining(policy, now) };
}

/**
 * Available when 90 or fewer calendar days remain, which is from the start of opensOn: the end date
 * less 90 days, in the policy's zone.
 */
export function renewalAvailability(policy: PolicySummary, now: DateTime): RenewalAvailability {
  return {
    available: daysRemaining(policy, now) <= RENEWAL_WINDOW_DAYS,
    opensOn: termDate(policy.effectiveUntil, policy).startOf('day').minus({ days: RENEWAL_WINDOW_DAYS }),
  };
}

/** An Auto Hail-Safe policy, which renews through its own dialog rather than a renewal quote. */
export function isAhsProduct(policy: PolicySummary): boolean {
  return AHS_PRODUCTS.includes(policy.product?.name ?? '');
}
